import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider, IconButton, Tooltip, Button } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import LaunchIcon from '@material-ui/icons/Launch';
import GetAppIcon from '@material-ui/icons/GetApp';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';
import PdfPagesViewer from './PdfPagesViewer';

const ReportView = ({ content = '', pdfReportUrl = null }) => {
    const reportRef = React.useRef(null);
    const contentStr = typeof content === 'string' ? content : String(content || '');
    // Basic markdown-to-sections parser
    const sections = contentStr.split('###').map(s => s.trim()).filter(Boolean);
    const mainTitle = contentStr.match(/^# (.*)/m)?.[1] || "BRSR ANALYSIS";
    const subTitle = contentStr.match(/^## (.*)/m)?.[1] || "";

    const isMsplEsgContent = contentStr.includes('MSPL Enterprise Strategic Assessment') ||
        contentStr.includes('MSPL=ESG-Report.pdf') ||
        (contentStr.includes('MSPL') && contentStr.toLowerCase().includes('esg'));
    const activePdfUrl = pdfReportUrl || (isMsplEsgContent ? '/MSPL-ESG-Report.pdf' : null);

    const handleDownloadPdf = () => {
        if (!activePdfUrl) return;
        const link = document.createElement('a');
        link.href = activePdfUrl;
        link.download = 'MSPL=ESG-Report.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderTable = (sectionContent) => {
        // Simple logic to detect line-based peer data if the AI returns it
        const lines = sectionContent.split('\n').filter(l => l.includes(':'));
        if (lines.length < 2) return null;

        const data = lines.map(line => {
            const [name, rest] = line.split(':');
            const match = rest?.match(/([0-9.]+)/);
            return {
                name: name.trim(),
                value: match ? parseFloat(match[1]) : 0,
                fullText: rest.trim()
            };
        }).filter(d => d.value > 0);

        if (data.length === 0) return null;

        return (
            <Box mt={3} mb={4}>
                <TableContainer component={Paper} elevation={0} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                    <Table size="small">
                        <TableHead style={{ backgroundColor: '#0f172a' }}>
                            <TableRow>
                                <TableCell style={{ color: '#fff', fontWeight: 700 }}>Company</TableCell>
                                <TableCell style={{ color: '#fff', fontWeight: 700 }} align="right">Intensity Value</TableCell>
                                <TableCell style={{ color: '#fff', fontWeight: 700 }}>Assessment</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((row, idx) => (
                                <TableRow key={idx} hover style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                    <TableCell style={{ fontWeight: 700, color: '#1e293b' }}>{row.name}</TableCell>
                                    <TableCell align="right" style={{ fontWeight: 800, color: '#2563eb' }}>{row.value}</TableCell>
                                    <TableCell style={{ fontSize: '12px', color: '#64748b' }}>{row.fullText.split('(')[1]?.replace(')', '') || 'Reported'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box height={300} mt={4} p={2} bgcolor="#fff" borderRadius={8} border="1px solid #e2e8f0">
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#64748b', display: 'block', mb: 2 }}>
                        Intensity Comparison Chart
                    </Typography>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                            <RechartsTooltip
                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                                cursor={{ fill: '#f1f5f9' }}
                            />
                            <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={40}>
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={index === 0 ? '#3b82f6' : '#94a3b8'} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Box>
        );
    };

    const handleOpenInNewWindow = () => {
        if (activePdfUrl) {
            window.open(activePdfUrl, '_blank');
            return;
        }
        if (!reportRef.current) return;
        const reportHtml = reportRef.current.innerHTML;
        const newWindow = window.open('', '_blank', 'width=1100,height=900,menubar=no,toolbar=no,location=no');

        const styleText = `
            body { font-family: 'Inter', -apple-system, sans-serif; padding: 50px; color: #334155; line-height: 1.6; background: #fff; }
            h4, h6 { margin-bottom: 8px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; font-size: 14px; }
            th { background-color: #0f172a; color: white; padding: 12px; text-align: left; }
            td { padding: 12px; border-bottom: 1px solid #f1f5f9; }
            hr { margin: 32px 0; border: none; border-top: 1px solid #f1f5f9; }
            .recharts-responsive-container { height: 320px !important; margin: 24px 0; border: 1px solid #f1f5f9; padding: 16px; border-radius: 12px; background: #fafafa; }
            @media print { .no-print { display: none; } }
            svg { max-width: 100%; height: auto; }
        `;

        newWindow.document.write(`
            <html>
                <head>
                    <title>${mainTitle}</title>
                    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
                    <style>${styleText}</style>
                </head>
                <body>
                    <div style="max-width: 1200px; margin: 0 auto;">
                        ${reportHtml}
                    </div>
                    <script>
                        setTimeout(() => {
                           const actions = document.querySelectorAll('.report-actions');
                           actions.forEach(a => a.remove());
                        }, 50);
                    </script>
                </body>
            </html>
        `);
        newWindow.document.close();
    };

    return (
        <Box ref={reportRef} p={4} bgcolor="#fff" color="#334155" borderRadius={2} style={{ fontFamily: '"Inter", sans-serif', boxShadow: 'inset 0 0 0 1px #e2e8f0' }}>
            <Box mb={4} textAlign="center" style={{ position: 'relative' }}>
                <Box className="report-actions" style={{ position: 'absolute', right: 0, top: 0, display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {activePdfUrl && (
                        <Tooltip title="Download MSPL-ESG-Report.pdf">
                            <IconButton
                                onClick={handleDownloadPdf}
                                size="small"
                                style={{
                                    backgroundColor: '#ecfdf5',
                                    border: '1px solid #a7f3d0',
                                    padding: '8px',
                                    borderRadius: '10px'
                                }}
                            >
                                <GetAppIcon style={{ fontSize: '18px', color: '#059669' }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Launch Report in New Window">
                        <IconButton
                            onClick={handleOpenInNewWindow}
                            size="small"
                            style={{
                                backgroundColor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                padding: '8px',
                                borderRadius: '10px'
                            }}
                        >
                            <LaunchIcon style={{ fontSize: '18px', color: '#2563eb' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Typography variant="overline" style={{ color: '#b59410', fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
                    {mainTitle.replace('ESG ANALYSIS REPORT:', '').trim() || "ESG ANALYSIS REPORT"}
                </Typography>
                <Typography variant="h4" style={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-1px' }}>
                    {subTitle}
                </Typography>
                <Box mt={3} height={3} width={80} bgcolor="#1e293b" mx="auto" borderRadius={2} />
            </Box>

            {/* Continuous PDF Pages Viewer for MSPL-ESG-Report.pdf */}
            {activePdfUrl ? (
                <Box mt={2} mb={4}>
                    <Box mb={4} p={3} bgcolor="#f8fafc" borderRadius={12} border="1px solid #e2e8f0">
                        <Typography variant="subtitle2" style={{ fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                            Executive Summary & Compliance Assessment
                        </Typography>
                        <Typography variant="body2" style={{ color: '#334155', lineHeight: 1.7, fontSize: '14px' }}>
                            MSPL demonstrates robust alignment with regulatory standards including BRSR Core and international ESG reporting frameworks. Below is the full verified PDF analysis report document displayed page-by-page directly within your screen.
                        </Typography>
                    </Box>

                    <PdfPagesViewer pdfUrl={activePdfUrl} />
                </Box>
            ) : (
                sections.map((section, idx) => {
                    const lines = section.split('\n').filter(Boolean);
                    const title = lines[0] || '';
                    const sectionBody = lines.slice(1).join('\n');

                    const isDataSection = /DATA|PEER|METRICS|COMPARISON/i.test(title);

                    return (
                        <Box key={idx} mb={6}>
                            <Box display="flex" alignItems="center" mb={2}>
                                <Box width={4} height={24} bgcolor="#b59410" mr={2} borderRadius={2} />
                                <Typography variant="h6" style={{ color: '#1e293b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '16px' }}>
                                    {title.replace(/^###\s*/, '').trim()}
                                </Typography>
                            </Box>

                            <Typography variant="body1" style={{ lineHeight: 1.8, color: '#334155', fontSize: '15px', whiteSpace: 'pre-wrap', marginBottom: '16px' }}>
                                {sectionBody}
                            </Typography>

                            {isDataSection && renderTable(sectionBody)}
                            <Divider style={{ marginTop: '32px', backgroundColor: '#f1f5f9' }} />
                        </Box>
                    );
                })
            )}

            <Box mt={8} p={3} bgcolor="#f8fafc" borderRadius={2} textAlign="center" border="1px solid #e2e8f0">
                <Typography variant="caption" style={{ color: '#64748b', fontWeight: 500 }}>
                    This report represents a strategic AI synthesis of regulatory disclosures (BRSR) and performance metrics.
                    Copyright © 2026 AmploFly 4.0 Intelligence.
                </Typography>
            </Box>
        </Box>
    );
};

export default ReportView;
